// src/hooks/useBusinessDay.js
// Учётный день активного сервера — опорная дата для всех периодов (см. utils/periods.js).
// null — ещё грузится: страницы в это время показывают загрузку, а не нули за календарный день.
import { useEffect, useState } from 'react';
import { fetchBusinessDay } from '../services/dataService';
import { todayMsk } from '../utils/periods';

// Бэкенд кеширует ответ на 60 с — и мы не спрашиваем чаще, в том числе при переходах между разделами
const FRESH_MS = 60 * 1000;
let cached = null; // { serverId, day, at }
let pending = null; // { serverId, promise }

function activeServerId() {
  try {
    return JSON.parse(localStorage.getItem('activeServer') || 'null')?.id ?? null;
  } catch {
    return null;
  }
}

function isFresh(serverId) {
  return cached && cached.serverId === serverId && Date.now() - cached.at < FRESH_MS;
}

function loadBusinessDay(serverId) {
  if (pending && pending.serverId === serverId) return pending.promise;
  const token = localStorage.getItem('token');
  const promise = (token ? fetchBusinessDay(token) : Promise.resolve(null))
    .catch((err) => {
      console.warn('Учётный день не получен, берём дату по MSK:', err?.message || err);
      return null;
    })
    .then((day) => {
      const result = day || todayMsk();
      cached = { serverId, day: result, at: Date.now() };
      return result;
    })
    .finally(() => {
      if (pending?.promise === promise) pending = null;
    });
  pending = { serverId, promise };
  return promise;
}

export function useBusinessDay() {
  const [day, setDay] = useState(() => (isFresh(activeServerId()) ? cached.day : null));

  useEffect(() => {
    let alive = true;

    const refresh = (force = false) => {
      const serverId = activeServerId();
      if (!force && isFresh(serverId)) {
        setDay(cached.day);
        return;
      }
      loadBusinessDay(serverId).then((result) => {
        if (alive) setDay(result);
      });
    };

    // Смена сервера — сразу новый день; возврат во вкладку — если ответ устарел (смены могли закрыться).
    // activeServerChanged шлётся и без смены (каждый fetchActiveServer) — реагируем только на другой id
    const onServerChanged = () => {
      if (cached && cached.serverId === activeServerId()) return;
      setDay(null);
      refresh(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    refresh();
    window.addEventListener('activeServerChanged', onServerChanged);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('activeServerChanged', onServerChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return day;
}
